import { yupResolver } from '@hookform/resolvers/yup';
import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { campaignApi } from '../../../../api/campaign';
import { translateKey } from 'src/locales/translate';


interface CampaignStep1BasicProps {
  initialValues: {
    name: string;
    description: string;
    keywords: string[];
  };
  organizationId: string;
  onNext: (values: {
    name: string;
    description: string;
    keywords: string[];
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const schema = Yup.object().shape({
  name: Yup.string()
    .required('Nazwa kampanii jest wymagana')
    .min(3, translateKey("hardcoded.nazwa_musi_miec_minimum_3_znaki"))
    .max(100, translateKey("hardcoded.nazwa_moze_miec_maksymalnie_100_znakow")),
  description: Yup.string().max(500, translateKey("hardcoded.opis_moze_miec_maksymalnie_500_znakow")),
});

export default function CampaignStep1Basic({
  initialValues,
  organizationId,
  onNext,
  onCancel,
  isLoading,
}: CampaignStep1BasicProps) {
  const [keywords, setKeywords] = useState<string[]>(initialValues.keywords);
  const [keywordInput, setKeywordInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: initialValues.name,
      description: initialValues.description,
    },
  });

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim().toLowerCase();
    if (trimmed && !keywords.includes(trimmed)) {
      setKeywords([...keywords, trimmed]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setKeywords(keywords.filter((k) => k !== keywordToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleGenerateKeywords = async () => {
    setIsGenerating(true);
    try {
      const response =
        await campaignApi.campaignControllerGenerateKeywordsForOrganization({
          organizationId,
        });
      const newKeywords = response.data.keywords || [];
      setKeywords((prev) => Array.from(new Set([...prev, ...newKeywords])));
    } catch (error) {
      console.error('Failed to generate keywords:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const onSubmit = (data: { name: string; description?: string }) => {
    onNext({
      name: data.name,
      description: data.description || '',
      keywords,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Nazwa kampanii"
              fullWidth
              error={!!errors.name}
              helperText={errors.name?.message}
              placeholder="np. Kampania letnia 2026"
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Opis (opcjonalny)"
              fullWidth
              multiline
              rows={3}
              error={!!errors.description}
              helperText={errors.description?.message}
              placeholder={translateKey("hardcoded.krotki_opis_celu_kampanii")}
            />
          )}
        />

        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2">{translateKey("hardcoded.slowa_kluczowe_2")}</Typography>
            <Button
              size="small"
              startIcon={
                isGenerating ? (
                  <CircularProgress size={16} />
                ) : (
                  <AutoAwesomeIcon />
                )
              }
              onClick={handleGenerateKeywords}
              disabled={isGenerating}
            >
              Generuj z Asystentem Vetsi
            </Button>
          </Box>

          <TextField
            fullWidth
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={translateKey("hardcoded.wpisz_slowo_kluczowe_i_nacisnij_enter")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleAddKeyword} size="small">
                    <AddIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 2,
              minHeight: 40,
            }}
          >
            {keywords.map((keyword) => (
              <Chip
                key={keyword}
                label={keyword}
                onDelete={() => handleRemoveKeyword(keyword)}
                size="small"
              />
            ))}
            {keywords.length === 0 && (
              <Typography variant="body2" color="text.secondary">{translateKey("hardcoded.dodaj_slowa_kluczowe_lub_wygeneruj_je_automatycznie")}</Typography>
            )}
          </Box>
        </Box>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
          <Button variant="outlined" color="inherit" onClick={onCancel}>{translateKey("common.close")}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || keywords.length === 0}
          >
            {isLoading ? <CircularProgress size={24} /> : translateKey("quickBooking.next")}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
